import type { Theme } from "@/types";

/** Primary 1–3 (`color1`–`color3`) and Secondary 1–6 (`color4`–`color9`) from `/theme`. */
export interface SiteThemeColors {
  color1?: string;
  color2?: string;
  color3?: string;
  color4?: string;
  color5?: string;
  color6?: string;
  color7?: string;
  color8?: string;
  color9?: string;
  brandColor?: string;
}

const COLOR_KEYS = [
  "color1",
  "color2",
  "color3",
  "color4",
  "color5",
  "color6",
  "color7",
  "color8",
  "color9",
  "brandColor"
] as const;

const HEX_COLOR = /^#(?:[0-9a-f]{3}){1,2}$/i;

const readHex = (value: unknown): string | undefined => {
  if (typeof value !== "string") return undefined;
  const hex = value.trim();
  return HEX_COLOR.test(hex) ? hex : undefined;
};

export const readSiteThemeColors = (
  theme?: Partial<Theme> | SiteThemeColors | null
): SiteThemeColors => {
  if (!theme) return {};

  return COLOR_KEYS.reduce<SiteThemeColors>((colors, key) => {
    const hex = readHex(theme[key]);
    if (hex) colors[key] = hex;
    return colors;
  }, {});
};

/** ehqv2 `Layout#smart_text_color` — black on light Primary 1, white on dark. */
export const contrastTextColor = (hex?: string): "#000" | "#fff" => {
  if (!hex || !HEX_COLOR.test(hex)) return "#fff";

  let value = hex.slice(1);
  if (value.length === 3) {
    value = value
      .split("")
      .map((digit) => `${digit}${digit}`)
      .join("");
  }

  const r = parseInt(value.slice(0, 2), 16);
  const g = parseInt(value.slice(2, 4), 16);
  const b = parseInt(value.slice(4, 6), 16);
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  return yiq >= 128 ? "#000" : "#fff";
};

/** ehqv2 `resolved_palette_brand_color`: brand, else Primary 1. */
export const resolveBrandColor = (
  theme?: Partial<Theme> | SiteThemeColors | null
): string | undefined => {
  const colors = readSiteThemeColors(theme);
  return colors.brandColor || colors.color1;
};

/**
 * Project accent: dark Primary 2, else brand, else Primary 1.
 * Light Primary 2 (Classic default white) is skipped so titles stay readable.
 */
export const resolveAccentColor = (
  theme?: Partial<Theme> | SiteThemeColors | null
): string | undefined => {
  const colors = readSiteThemeColors(theme);
  if (colors.color2 && contrastTextColor(colors.color2) === "#fff") {
    return colors.color2;
  }
  return colors.brandColor || colors.color1;
};

export const siteBrandOrCurrent = (
  theme?: Partial<Theme> | SiteThemeColors | null,
  fallback = "currentColor"
): string => resolveBrandColor(theme) || fallback;
